import { createApiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '@clerk/clerk-react';
import type { WorkoutAttribution } from '@ergshoot/shared';
import { useCallback, useMemo, useRef } from 'react';

/**
 * Hook that provides members API methods with proper Clerk token handling.
 * Uses refs and callbacks to ensure stable function references across renders.
 * Passes ?affiliate_id from activeProfile to all requests to support multi-gym switching.
 */
export function useMembersApi() {
  const { getToken } = useAuth();
  const activeProfile = useAuthStore((s) => s.activeProfile);

  // Store getToken in a ref to avoid dependency changes
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // Store activeProfile in a ref so API calls always use latest value
  const activeProfileRef = useRef(activeProfile);
  activeProfileRef.current = activeProfile;

  // Create a stable token getter that always uses the latest getToken
  const stableGetToken = useCallback(() => getTokenRef.current(), []);

  const membersApi = useMemo(() => {
    const client = createApiClient(stableGetToken);

    // Helper: append affiliate_id query param when available
    const affiliateParam = () => {
      const id = activeProfileRef.current?.affiliate_id;
      return id ? `?affiliate_id=${encodeURIComponent(id)}` : '';
    };
    const affiliateAppend = (existing: string) => {
      const id = activeProfileRef.current?.affiliate_id;
      if (!id) return existing;
      const sep = existing.includes('?') ? '&' : '?';
      return `${existing}${sep}affiliate_id=${encodeURIComponent(id)}`;
    };

    return {
      getGyms: () =>
        client.get<{
          gyms: Array<{
            affiliate_id: string;
            name: string;
            member_role: string;
            joined_at: string | null;
          }>;
        }>('/members/gyms'),

      getDashboard: () =>
        client.get<{
          member: { id: string; name: string; affiliate_name: string };
          recent_scores: Array<{
            workout_name: string;
            score_value: number;
            score_unit: string;
            logged_at: string;
          }>;
        }>(`/members/dashboard${affiliateParam()}`),

      getWorkouts: (params?: { cursor?: string; limit?: number }) => {
        const searchParams = new URLSearchParams();
        if (params?.cursor) searchParams.set('cursor', params.cursor);
        if (params?.limit) searchParams.set('limit', String(params.limit));
        const qs = searchParams.toString();
        return client.get<{
          workouts: Array<{
            id: string;
            name: string;
            description: string | null;
            movements: string[];
            equipment: string[];
            scoring_type: string;
            scoring_direction: string;
            scheduled_date: string;
            track: { id: string; name: string };
            attribution?: WorkoutAttribution | null;
            created_at?: string | null;
            created_by_username?: string | null;
            member_score: {
              id: string;
              value: number | null;
              notes: string | null;
              logged_at: string | null;
            } | null;
          }>;
          pagination: { has_more: boolean; next_cursor: string | null };
        }>(affiliateAppend(qs ? `/members/workouts?${qs}` : '/members/workouts'));
      },

      logScore: (
        workoutId: string,
        data: { value: number | string; division?: string; notes?: string },
        idempotencyKey?: string
      ) =>
        client.post<{ score: { id: string } }>(
          `/members/scores${affiliateParam()}`,
          {
            workout_id: workoutId,
            ...data,
          },
          { headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined }
        ),

      updateScore: (
        scoreId: string,
        data: { value?: number | string; notes?: string },
        idempotencyKey?: string
      ) =>
        client.patch<{ score: { id: string } }>(`/members/scores/${scoreId}`, data, {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        }),

      getLeaderboard: (workoutId: string, params?: { scope?: 'gym' | 'global' }) => {
        const searchParams = new URLSearchParams();
        if (params?.scope) searchParams.set('scope', params.scope);
        const qs = searchParams.toString();
        // Response shape matches the backend at members/index.ts:1188+ — each
        // entry has a nested `score` object with id/value/notes/logged_at.
        // Previously declared as flat score_value/score_unit which caused a
        // silent FE/BE mismatch; fixed 2026-04-08 as a prerequisite for
        // FEAT-APR07-013 (score vote buttons on leaderboard).
        return client.get<{
          workout: {
            id: string;
            name: string;
            scoring_type: string;
            scoring_direction: string;
          };
          leaderboard: Array<{
            rank: number;
            user_id: string;
            user_name: string;
            score: {
              id: string;
              value: number;
              notes: string | null;
              logged_at: string;
              approval_status?: 'pending' | 'approved' | 'rejected';
            };
            is_current_user: boolean;
            affiliate_name?: string;
          }>;
          member_position?: {
            rank: number | null;
            total: number;
            has_score: boolean;
            message?: string;
          };
        }>(
          affiliateAppend(
            qs ? `/members/leaderboard/${workoutId}?${qs}` : `/members/leaderboard/${workoutId}`
          )
        );
      },

      getEvents: () =>
        client.get<{
          events: Array<{
            id: string;
            name: string;
            event_date: string;
            start_time: string | null;
            end_time: string | null;
            event_timezone: string | null;
            start_at_utc: string | null;
            visibility: 'internal' | 'public';
            is_signed_up: boolean;
            participant_count: number;
          }>;
        }>(`/members/events${affiliateParam()}`),

      registerForEvent: (eventId: string) =>
        client.post<{ registration: { id: string } }>(
          `/members/events/${eventId}/register${affiliateParam()}`
        ),

      withdrawFromEvent: (eventId: string) =>
        client.delete(`/members/events/${eventId}/withdraw${affiliateParam()}`),

      getProfile: () =>
        client.get<{
          profile: {
            id: string;
            name: string;
            email: string;
            affiliate_name: string;
            joined_at: string;
          };
        }>(`/members/profile${affiliateParam()}`),

      updateProfile: (data: { name?: string }) =>
        client.patch<{ profile: { id: string; name: string } }>('/members/profile', data),
    };
  }, [stableGetToken]);

  return membersApi;
}
